const MAX_VISIBLE_TOASTS = 3;
const TOAST_QUEUE_ID = "toast-queue";
const TOAST_VIEWPORT_ID = "toast-viewport";
const TOAST_EXIT_DURATION_MS = 200;
const TOAST_QUEUE_HANDOFF_DELAY_MS = 120;

let visibleToasts: Array<HTMLElement> = [];
const pendingToasts: Array<HTMLElement> = [];
let dismissTimer: number | undefined;
let dismissStartedAt = 0;
let dismissRemainingMs = 0;
let dismissToastElement: HTMLElement | undefined;
let hoverPaused = false;
let windowPaused = false;

const ensureToastElement = (id: string, className: string) => {
  const existing = document.getElementById(id);
  if (existing) {
    return existing;
  }

  const el = document.createElement("div");
  el.id = id;
  el.className = className;
  document.body.appendChild(el);
  return el;
};

const ensureToastQueue = () => {
  const queue = ensureToastElement(TOAST_QUEUE_ID, "hidden");
  queue.hidden = true;
  return queue;
};

const ensureToastViewport = () => {
  return ensureToastElement(
    TOAST_VIEWPORT_ID,
    "pointer-events-none fixed bottom-4 left-1/2 z-[9999] flex w-full max-w-sm -translate-x-1/2 flex-col gap-2 px-4 md:left-auto md:right-4 md:w-96 md:max-w-[calc(100vw-2rem)] md:translate-x-0 md:px-0",
  );
};

const clearDismissTimer = () => {
  if (dismissTimer !== undefined) {
    window.clearTimeout(dismissTimer);
    dismissTimer = undefined;
  }
};

const isPaused = () => hoverPaused || windowPaused;

const scheduleDismissTimer = (duration?: number) => {
  if (dismissTimer !== undefined || isPaused()) {
    return;
  }

  const toast = visibleToasts.find((visibleToast) => !visibleToast.dataset.toastDismissed);
  if (!toast) {
    return;
  }

  dismissToastElement = toast;
  dismissRemainingMs = duration ?? Number(toast.dataset.toastDuration ?? "4000");
  dismissStartedAt = Date.now();
  dismissTimer = window.setTimeout(() => dismissToast(toast), dismissRemainingMs);
};

const pauseDismissTimer = () => {
  if (dismissTimer !== undefined) {
    clearDismissTimer();
    dismissRemainingMs = Math.max(0, dismissRemainingMs - (Date.now() - dismissStartedAt));
  }
};

const refreshDismissTimer = () => {
  if (isPaused()) {
    pauseDismissTimer();
    return;
  }

  if (dismissTimer === undefined) {
    scheduleDismissTimer(dismissRemainingMs || undefined);
  }
};

const syncHoverPauseState = () => {
  hoverPaused = visibleToasts.some((toast) => !toast.dataset.toastDismissed && toast.matches(":hover"));
  refreshDismissTimer();
};

const addToastPauseListeners = (toast: HTMLElement) => {
  toast.addEventListener("mouseenter", () => {
    hoverPaused = true;
    refreshDismissTimer();
  });
  toast.addEventListener("mouseleave", () => {
    window.setTimeout(syncHoverPauseState);
  });
};

const showNextToast = () => {
  const viewport = ensureToastViewport();

  while (visibleToasts.length < MAX_VISIBLE_TOASTS && pendingToasts.length > 0) {
    const toast = pendingToasts.shift();
    if (!toast) {
      return;
    }

    visibleToasts.push(toast);
    viewport.appendChild(toast);

    const closeButton = toast.querySelector("[data-toast-close]");
    closeButton?.addEventListener("click", () => dismissToast(toast), {once: true});
    addToastPauseListeners(toast);

    requestAnimationFrame(() => {
      toast.classList.remove("translate-y-2", "opacity-0");
      toast.classList.add("translate-y-0", "opacity-100");
    });

    scheduleDismissTimer();
  }
};

const enqueueToast = (toast: HTMLElement) => {
  toast.remove();
  pendingToasts.push(toast);
  showNextToast();
};

const dismissToast = (toast: HTMLElement) => {
  if (toast.dataset.toastDismissed) {
    return;
  }

  toast.dataset.toastDismissed = "true";
  if (dismissToastElement === toast) {
    clearDismissTimer();
    dismissToastElement = undefined;
    dismissStartedAt = 0;
    dismissRemainingMs = 0;
  }

  toast.classList.remove("translate-y-0", "opacity-100");
  toast.classList.add("translate-y-2", "opacity-0");

  window.setTimeout(() => {
    toast.remove();
    visibleToasts = visibleToasts.filter((visibleToast) => visibleToast !== toast);
    window.setTimeout(() => {
      syncHoverPauseState();
      showNextToast();
      scheduleDismissTimer();
    }, TOAST_QUEUE_HANDOFF_DELAY_MS);
  }, TOAST_EXIT_DURATION_MS);
};

const observeToastQueue = (queue: HTMLElement) => {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) {
          continue;
        }

        if (node.matches("[data-toast]")) {
          enqueueToast(node);
          continue;
        }

        node.querySelectorAll<HTMLElement>("[data-toast]").forEach(enqueueToast);
      }
    }
  });

  observer.observe(queue, {childList: true});
};

export const initToastManager = () => {
  if (!document.body) {
    document.addEventListener("DOMContentLoaded", initToastManager, {once: true});
    return;
  }

  const queue = ensureToastQueue();
  ensureToastViewport();

  if (queue.dataset.toastQueueInitialized) {
    return;
  }

  queue.dataset.toastQueueInitialized = "true";
  queue.querySelectorAll<HTMLElement>("[data-toast]").forEach(enqueueToast);
  observeToastQueue(queue);
  window.addEventListener("blur", () => {
    windowPaused = true;
    refreshDismissTimer();
  });
  window.addEventListener("focus", () => {
    windowPaused = false;
    syncHoverPauseState();
  });
};
